import _ from "lodash";
import { MethodCategories, MethodColors } from "data/methods";

export interface TreemapMethod {
  name: string;
  value: number;
  category: string;
}

export interface TreemapCategory {
  name: string;
}

export interface TreemapDataPoint {
  x: string;
  y: number;
}

export interface TreemapSeries {
  name: string;
  data: TreemapDataPoint[];
  color: string;
}

export default class Treemap {
  /**
   * Build treemap series from categories and methods
   * Groups methods by category and formats for ApexCharts treemap
   * @param categories - Array of category objects
   * @param methods - Array of method objects with category, name, and value
   * @returns Array of series objects for ApexCharts
   */
  static buildSeries(
    categories: TreemapCategory[],
    methods: TreemapMethod[]
  ): TreemapSeries[] {
    // Group methods by category
    const groupedMethods = methods.reduce((acc, method) => {
      if (!acc[method.category]) {
        acc[method.category] = [];
      }
      acc[method.category].push({
        x: method.name,
        y: method.value,
      });
      return acc;
    }, {} as Record<string, TreemapDataPoint[]>);

    // Convert grouped data into ApexCharts format
    return categories.map((cat) => {
      const defaultColor = _.get(MethodColors, MethodCategories.Other, {});
      const categoryColor = _.get(MethodColors, cat.name, defaultColor);
      return {
        name: cat.name,
        data: groupedMethods[cat.name] || [], // Fallback to empty array if no methods
        color: (categoryColor as { [key: number]: string })[500] || "#6c757d",
      };
    });
  }

  /**
   * Calculate total value from methods
   * @param methods - Array of method objects with value property
   * @returns Total sum of all method values
   */
  static calculateTotal(methods: TreemapMethod[]): number {
    return _.sumBy(methods, "value");
  }

  /**
   * Format tooltip value with percentage
   * @param value - Method value
   * @param total - Total value across all methods
   * @returns Formatted percentage string
   */
  static formatPercentage(value: number, total: number): string {
    return ((value / total) * 100).toFixed(1);
  }
}
